import classNames from "classnames";
import { XCloseIcon } from "@vetty/icons";
import { Modal } from "@/components";
import { formatIssueTimestamp, getIssueTypeMeta } from "../../_constants/issues.const";
import { Issue } from "../../_types/issues.types";
import styles from "./IssueDetailsModal.module.scss";

type IssueDetailsModalProps = {
  issue: Issue | null;
  isOpen: boolean;
  isUpdatingReadStatus: boolean;
  onClose: () => void;
};

export const IssueDetailsModal = ({
  issue,
  isOpen,
  isUpdatingReadStatus,
  onClose,
}: IssueDetailsModalProps) => {
  if (!issue) return null;

  const typeMeta = getIssueTypeMeta(issue.errorType);
  const reporterName = issue.reporterName || "Anonymous";
  const reporterEmail = issue.reporterEmail || "No email";
  const hasReadDetails = Boolean(issue.read && issue.readByName && issue.readAt);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles["comp"]}>
        <div className={styles["comp__header"]}>
          <div className={styles["comp__title-row"]}>
            <div className={styles["comp__heading"]}>{reporterName}</div>
            <span
              className={classNames(
                styles["comp__badge"],
                styles[`comp__badge--${typeMeta.variant}`]
              )}
            >
              {typeMeta.label}
            </span>
          </div>

          <button
            type="button"
            className={styles["comp__close-button"]}
            onClick={onClose}
            aria-label="Close issue details"
          >
            <XCloseIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>

        <div className={styles["comp__meta-grid"]}>
          <div className={styles["comp__meta-item"]}>
            <span className={styles["comp__meta-label"]}>Email</span>
            <span className={styles["comp__meta-value"]}>{reporterEmail}</span>
          </div>

          <div className={styles["comp__meta-item"]}>
            <span className={styles["comp__meta-label"]}>Status</span>
            <div className={styles["comp__meta-stack"]}>
              <span className={styles["comp__meta-value"]}>{issue.read ? "Read" : "Unread"}</span>
              {hasReadDetails && (
                <span className={styles["comp__meta-note"]}>
                  Read by {issue.readByName} at {formatIssueTimestamp(issue.readAt as number)}
                </span>
              )}
              {isUpdatingReadStatus && (
                <span className={styles["comp__meta-note"]}>Updating read status...</span>
              )}
            </div>
          </div>

          <div className={styles["comp__meta-item"]}>
            <span className={styles["comp__meta-label"]}>Created at</span>
            <span className={styles["comp__meta-value"]}>{formatIssueTimestamp(issue.createdAt)}</span>
          </div>

          <div className={styles["comp__meta-item"]}>
            <span className={styles["comp__meta-label"]}>Reported at</span>
            <span className={styles["comp__meta-value"]}>{formatIssueTimestamp(issue.reportedAt)}</span>
          </div>

          <div className={styles["comp__meta-item"]}>
            <span className={styles["comp__meta-label"]}>Model slug</span>
            <span className={styles["comp__meta-value"]}>{issue.modelSlug}</span>
          </div>

          {issue.modelVersion && (
            <div className={styles["comp__meta-item"]}>
              <span className={styles["comp__meta-label"]}>Model version</span>
              <span className={styles["comp__meta-value"]}>{issue.modelVersion}</span>
            </div>
          )}

          <div className={styles["comp__meta-item"]}>
            <span className={styles["comp__meta-label"]}>Structure name</span>
            <span className={styles["comp__meta-value"]}>{issue.structureName}</span>
          </div>

          {issue.structureId && (
            <div className={styles["comp__meta-item"]}>
              <span className={styles["comp__meta-label"]}>Structure ID</span>
              <span className={styles["comp__meta-value"]}>{issue.structureId}</span>
            </div>
          )}

          {issue.structureCollection && (
            <div className={styles["comp__meta-item"]}>
              <span className={styles["comp__meta-label"]}>Collection</span>
              <span className={styles["comp__meta-value"]}>{issue.structureCollection}</span>
            </div>
          )}

          {issue.latinName && (
            <div className={styles["comp__meta-item"]}>
              <span className={styles["comp__meta-label"]}>Latin name</span>
              <span className={styles["comp__meta-value"]}>{issue.latinName}</span>
            </div>
          )}
        </div>

        <div className={styles["comp__content"]}>
          <div className={styles["comp__meta-label"]}>Description</div>
          <p>{issue.description}</p>
        </div>
      </div>
    </Modal>
  );
};
